import { supportPages } from "./navigation.ts";

/**
 * Which page the address bar is asking for.
 *
 * The same mechanism the other consoles use, and for the same two reasons. "Come back to the
 * page you were on" needs the page written down somewhere that survives leaving for the identity
 * provider and returning, and a URL is that somewhere. It is also what makes a page shareable
 * between two agents looking at the same case — which on this console is the normal way of
 * working, because a case that needs a supervisor is a case being handed to one.
 *
 * A query parameter rather than a path, for one practical reason: a path needs the server to answer
 * every path with the application's document, and this console is served as static files from a
 * gateway nobody here configures. `/?page=cases` is one document at one path and needs nothing.
 */

export const PAGE_PARAMETER = "page";

/**
 * Where an agent lands with nothing asked for.
 *
 * `cases` is the design's own first rail item, it is built, and it is the page a support shift
 * starts on — every other page in this console is reached from a case. Taken from the list rather
 * than written, so a reordering moves it.
 */
export const DEFAULT_PAGE_ID: string = supportPages[0]?.id ?? "cases";

const KNOWN_PAGES: ReadonlySet<string> = new Set(supportPages.map((page) => page.id));

/**
 * Read the page out of a URL, ignoring anything that is not a page.
 *
 * An unknown identifier falls back to the default rather than rendering the shell's "No such page",
 * because the two mean different things to the agent who sees them: a stale bookmark should open
 * the console, and a page the shell knows about but this agent cannot reach should say so. The
 * second is the shell's job and it still happens.
 */
export function pageIdIn(url: string): string {
  const asked = new URL(url).searchParams.get(PAGE_PARAMETER);
  return asked !== null && KNOWN_PAGES.has(asked) ? asked : DEFAULT_PAGE_ID;
}

/**
 * The same URL, asking for a different page.
 *
 * Everything else in the address is preserved. That matters more than it looks: this is also the URL
 * the sign-in flow stores as its return address, so dropping a query parameter here would silently
 * drop it from what an agent comes back to.
 */
export function urlForPage(url: string, pageId: string): string {
  const next = new URL(url);
  if (pageId === DEFAULT_PAGE_ID) next.searchParams.delete(PAGE_PARAMETER);
  else next.searchParams.set(PAGE_PARAMETER, pageId);
  return next.toString();
}
